import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, Shield, Activity, FileText, AlertTriangle, CheckCircle, 
  XCircle, Key, Trash2, Lock, Unlock, RefreshCw, Search, 
  Clock, UserCheck, UserX, ChevronLeft, ChevronRight, Plus, X, Settings,
  BarChart3, ClipboardList, MapPin, Receipt, AlertCircle, Link2, PenTool, Bell,
  Calendar, TrendingUp, ClipboardCheck, Download, Briefcase, Globe, ExternalLink
} from 'lucide-react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { computeWeeklyMetrics } from '../utils/visitCalculations';
import { toast } from 'sonner';

// Lazy load sub-pages for better performance
const JobDuties = lazy(() => import('./JobDuties'));
const ESignatureManagement = lazy(() => import('./ESignatureManagement'));
const DeclinationsMap = lazy(() => import('./DeclinationsMap'));
const InvoiceVerification = lazy(() => import('./InvoiceVerification'));
const PermissionManagement = lazy(() => import('./PermissionManagementV2'));
const ExternalIntegrations = lazy(() => import('./ExternalIntegrations'));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Notification Settings Sub-component
function NotificationSettings({ settings, setSettings, personnel, saving, setSaving }) {
  const [localPersonnel, setLocalPersonnel] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!loaded) {
      const fetchData = async () => {
        try {
          const token = localStorage.getItem('token');
          const [settingsRes, personnelRes] = await Promise.all([
            axios.get(`${API}/admin/notification-settings`, { headers: { Authorization: `Bearer ${token}` } }),
            axios.get(`${API}/personnel?limit=10000`, { headers: { Authorization: `Bearer ${token}` } }),
          ]);
          setSettings(settingsRes.data || { time_off_recipients: [], onboarding_recipients: [], credential_expiry_recipients: [], applicant_note_recipients: [], personnel_note_recipients: [], agency_note_recipients: [] });
          setLocalPersonnel((personnelRes.data?.personnel || personnelRes.data || []).filter(p => p.email));
          setLoaded(true);
        } catch (e) { console.error('Failed to load notification settings:', e); }
      };
      fetchData();
    }
  }, [loaded, setSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/notification-settings`, settings, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Notification settings saved');
    } catch (e) { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  const toggleRecipient = (category, email) => {
    const list = settings[category] || [];
    setSettings({
      ...settings,
      [category]: list.includes(email) ? list.filter(e => e !== email) : [...list, email],
    });
  };

  const RecipientSection = ({ title, description, category }) => (
    <div className="space-y-3">
      <div>
        <h4 className="font-medium text-sm">{title}</h4>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
        {localPersonnel.map((p) => (
          <label key={p.id} className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={(settings[category] || []).includes(p.email || p.work_email)}
              onChange={() => toggleRecipient(category, p.email || p.work_email)}
              className="rounded"
            />
            <span>{p.first_name} {p.last_name}</span>
            <span className="text-xs text-muted-foreground ml-auto">{p.email || p.work_email}</span>
          </label>
        ))}
      </div>
      {(settings[category] || []).length > 0 && (
        <p className="text-xs text-muted-foreground">{(settings[category] || []).length} recipient(s) selected</p>
      )}
    </div>
  );

  return (
    <Card className="p-6" data-testid="notification-settings">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-semibold text-lg flex items-center gap-2"><Bell size={18} /> Notification Settings</h3>
          <p className="text-sm text-muted-foreground">Configure who receives email notifications for key events</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-notif-settings-btn">
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
      <Card className="mb-6 p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="text-sm text-blue-900">
            <p className="font-semibold flex items-center gap-1"><Bell size={14} /> Need more flexibility?</p>
            <p className="text-xs text-blue-800 mt-0.5">
              Use <b>Notification Rules</b> to create custom rules with weekly digests, multiple recipient sets per event, and full edit/pause/delete controls.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.location.href = '/admin/notification-rules'}
            data-testid="open-notification-rules-btn"
          >
            Open Notification Rules →
          </Button>
        </div>
      </Card>
      <div className="space-y-6 divide-y">
        <RecipientSection title="Applicant Notes" description="Notified when a note is added to any applicant record" category="applicant_note_recipients" />
        <div className="pt-6"><RecipientSection title="Personnel / Therapist Notes" description="Notified when a note is added to any personnel or therapist record" category="personnel_note_recipients" /></div>
        <div className="pt-6"><RecipientSection title="Agency Notes" description="Notified when a note is added to any agency record" category="agency_note_recipients" /></div>
        <div className="pt-6"><RecipientSection title="Time Off Requests" description="Notified when someone submits a time off / PTO request" category="time_off_recipients" /></div>
        <div className="pt-6"><RecipientSection title="Onboarding Completion" description="Notified when a new hire completes all onboarding items" category="onboarding_recipients" /></div>
        <div className="pt-6"><RecipientSection title="Credential Expiry Alerts" description="Notified when credentials are expiring or expired" category="credential_expiry_recipients" /></div>
      </div>
    </Card>
  );
}


// Credential Reminders Dashboard Sub-component
function CredentialReminders() {
  const [summary, setSummary] = useState(null);
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [triggeringReport, setTriggeringReport] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [triggeringDigest, setTriggeringDigest] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);

  const AVAILABLE_DAYS = [90, 60, 30, 14, 7, 3, 1];

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const [summaryRes, configRes, logsRes, digestRes] = await Promise.all([
          axios.get(`${API}/credentials/expiring-summary`, { headers }),
          axios.get(`${API}/credentials/reminder-config`, { headers }),
          axios.get(`${API}/credentials/reminder-logs?limit=20`, { headers }),
          axios.get(`${API}/admin/compliance-digest-preview`, { headers }),
        ]);
        setSummary(summaryRes.data);
        setConfig(configRes.data);
        setLogs(logsRes.data);
        setDigestPreview(digestRes.data);
      } catch (e) { console.error('Failed to load credential reminder data:', e); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  const toggleDay = (day) => {
    if (!config) return;
    const current = config.reminder_days || [];
    const updated = current.includes(day) ? current.filter(d => d !== day) : [...current, day];
    setConfig({ ...config, reminder_days: updated });
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/credentials/reminder-config`, { reminder_days: config.reminder_days }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Reminder schedule saved');
    } catch (e) { toast.error('Failed to save reminder config'); }
    finally { setSavingConfig(false); }
  };

  const triggerCheck = async () => {
    setTriggering(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/credentials/trigger-reminder-check`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message || 'Reminder check completed');
      // Refresh logs
      const logsRes = await axios.get(`${API}/credentials/reminder-logs?limit=20`, { headers: { Authorization: `Bearer ${token}` } });
      setLogs(logsRes.data);
    } catch (e) { toast.error('Failed to trigger reminder check'); }
    finally { setTriggering(false); }
  };

  const triggerReport = async () => {
    setTriggeringReport(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/credentials/trigger-admin-report`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message || 'Admin report sent');
    } catch (e) { toast.error('Failed to send admin report'); }
    finally { setTriggeringReport(false); }
  };

  const triggerDigest = async () => {
    setTriggeringDigest(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/trigger-compliance-digest`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message || 'Compliance digest sent');
    } catch (e) { toast.error('Failed to send compliance digest'); }
    finally { setTriggeringDigest(false); }
  };

  if (loading) return <Card className="p-6"><p className="text-muted-foreground">Loading credential reminders...</p></Card>;

  const counts = summary?.counts || {};

  return (
    <div className="space-y-6" data-testid="credential-reminders">
      {/* Expiring Summary Cards */}
      <Card className="p-6">
        <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
          <AlertTriangle size={18} /> Credential Expiry Overview
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center" data-testid="expired-count">
            <p className="text-3xl font-bold text-red-600">{counts.expired || 0}</p>
            <p className="text-sm text-red-700 mt-1">Expired</p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center" data-testid="expiring-30-count">
            <p className="text-3xl font-bold text-orange-600">{counts.expiring_30 || 0}</p>
            <p className="text-sm text-orange-700 mt-1">Within 30 days</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center" data-testid="expiring-60-count">
            <p className="text-3xl font-bold text-yellow-600">{counts.expiring_60 || 0}</p>
            <p className="text-sm text-yellow-700 mt-1">30–60 days</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center" data-testid="expiring-90-count">
            <p className="text-3xl font-bold text-blue-600">{counts.expiring_90 || 0}</p>
            <p className="text-sm text-blue-700 mt-1">60–90 days</p>
          </div>
        </div>

        {/* Detailed lists */}
        {(summary?.expired?.length > 0 || summary?.expiring_30?.length > 0) && (
          <div className="mt-6 space-y-4">
            {summary.expired?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-red-700 mb-2">Expired Credentials</h4>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {summary.expired.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-sm p-2 bg-red-50 rounded">
                      <span className="font-medium">{item.therapist_name}</span>
                      <span>{item.credential}</span>
                      <span className="text-red-600 font-semibold">{Math.abs(item.days_until_expiry)}d overdue</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {summary.expiring_30?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-orange-700 mb-2">Expiring Within 30 Days</h4>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {summary.expiring_30.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-sm p-2 bg-orange-50 rounded">
                      <span className="font-medium">{item.therapist_name}</span>
                      <span>{item.credential}</span>
                      <span className="text-orange-600 font-semibold">{item.days_until_expiry}d left</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Reminder Schedule Config */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-lg flex items-center gap-2"><Clock size={18} /> Reminder Schedule</h3>
            <p className="text-sm text-muted-foreground">
              Select when to send automated reminder emails to therapists.
              {config?.scheduler_active
                ? <span className="ml-2"><Badge className="bg-green-100 text-green-800">Scheduler Active</Badge></span>
                : <span className="ml-2"><Badge className="bg-red-100 text-red-800">Scheduler Inactive</Badge></span>}
            </p>
          </div>
          <Button onClick={saveConfig} disabled={savingConfig} size="sm" data-testid="save-reminder-config-btn">
            {savingConfig ? 'Saving...' : 'Save Schedule'}
          </Button>
        </div>
        <div className="flex flex-wrap gap-3">
          {AVAILABLE_DAYS.map(day => {
            const active = (config?.reminder_days || []).includes(day);
            return (
              <button
                key={day}
                onClick={() => toggleDay(day)}
                data-testid={`reminder-day-${day}`}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  active
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-muted/30 text-muted-foreground border-border hover:bg-muted/50'
                }`}
              >
                {day} days before
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Expired credentials also receive weekly reminders automatically. Runs daily at 8:00 AM.
        </p>
      </Card>

      {/* Compliance Digest Preview */}
      {digestPreview && (
        <Card className="p-6" data-testid="compliance-digest-preview">
          <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
            <FileText size={18} /> Weekly Compliance Digest
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Sent automatically every Monday at 8:00 AM to configured recipients.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-red-600">{digestPreview.expired_credentials}</p>
              <p className="text-xs text-red-700">Expired Creds</p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-orange-600">{digestPreview.expiring_30_credentials}</p>
              <p className="text-xs text-orange-700">Expiring 30d</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-gray-600">{digestPreview.late_notes_7d}</p>
              <p className="text-xs text-gray-700">Late Notes 7d</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-blue-600">{digestPreview.missed_visits_7d}</p>
              <p className="text-xs text-blue-700">Missed Visits 7d</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-green-600">{digestPreview.pending_pto}</p>
              <p className="text-xs text-green-700">Pending PTO</p>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">
              Total issues: <span className={digestPreview.total_issues > 0 ? 'text-red-600' : 'text-green-600'}>{digestPreview.total_issues}</span>
            </p>
            <Button onClick={triggerDigest} disabled={triggeringDigest} variant="outline" size="sm" data-testid="trigger-digest-btn">
              {triggeringDigest ? 'Sending...' : 'Send Digest Now'}
            </Button>
          </div>
        </Card>
      )}

      {/* Manual Triggers */}
      <Card className="p-6">
        <h3 className="font-semibold text-lg flex items-center gap-2 mb-4"><RefreshCw size={18} /> Manual Actions</h3>
        <div className="flex flex-wrap gap-3">
          <Button onClick={triggerCheck} disabled={triggering} variant="outline" data-testid="trigger-reminder-check-btn">
            {triggering ? 'Running...' : 'Run Reminder Check Now'}
          </Button>
          <Button onClick={triggerReport} disabled={triggeringReport} variant="outline" data-testid="trigger-admin-report-btn">
            {triggeringReport ? 'Sending...' : 'Send Admin Report Now'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          These actions send emails immediately. Use to test the workflow or handle urgent cases.
        </p>
      </Card>

      {/* Recent Reminder Logs */}
      <Card className="p-6">
        <h3 className="font-semibold text-lg flex items-center gap-2 mb-4"><Activity size={18} /> Recent Reminder Logs</h3>
        {logs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No reminder emails sent yet. Logs will appear here after the first run.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 pr-4">Therapist</th>
                  <th className="pb-2 pr-4">Credential</th>
                  <th className="pb-2 pr-4">Days</th>
                  <th className="pb-2 pr-4">Status</th>
                  <th className="pb-2">Sent At</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2 pr-4">{log.therapist_name}</td>
                    <td className="py-2 pr-4">{log.credential_type}</td>
                    <td className="py-2 pr-4">
                      <Badge variant={log.days_until_expiration < 0 ? 'destructive' : 'secondary'}>
                        {log.days_until_expiration < 0 ? `${Math.abs(log.days_until_expiration)}d overdue` : `${log.days_until_expiration}d left`}
                      </Badge>
                    </td>
                    <td className="py-2 pr-4">
                      <Badge variant={log.status === 'sent' ? 'default' : 'destructive'}>
                        {log.status || 'sent'}
                      </Badge>
                    </td>
                    <td className="py-2 text-muted-foreground">{new Date(log.sent_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// Helper to extract error message from API responses (handles Pydantic validation errors)
const getErrorMessage = (error, fallback = 'An error occurred') => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map(e => e.msg || e.message || String(e)).join(', ');
  }
  return fallback;
};

export default function AdminConsole() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'dashboard');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [weeklyMetrics, setWeeklyMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [diagnosticResults, setDiagnosticResults] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});
  const [permissionLevels, setPermissionLevels] = useState([]);
  
  // Filters
  const [auditPage, setAuditPage] = useState(0);
  const [auditFilter, setAuditFilter] = useState({ action: '', user_email: '', resource: '' });
  
  // Modal states
  const [showResetPassword, setShowResetPassword] = useState(null);
  const [tempPassword, setTempPassword] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  
  // New user form
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'user',
    permission_level_id: ''
  });

  // Notification settings
  const [notifSettings, setNotifSettings] = useState({ time_off_recipients: [], onboarding_recipients: [], credential_expiry_recipients: [] });
  const [notifPersonnel, setNotifPersonnel] = useState([]);
  const [savingNotif, setSavingNotif] = useState(false);

  // Onboarding digest
  const [onboardingDigest, setOnboardingDigest] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);

  // Get current user's effective permissions for admin console tabs
  const getCurrentUserPermissions = useCallback(async () => {
    try {
      const userStr = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      if (!userStr || !token) {
        console.log('No user or token in localStorage');
        return {};
      }
      
      const user = JSON.parse(userStr);
      console.log('AdminConsole user:', user);
      
      // If user has a permission_level_id, fetch the permission level details
      if (user.permission_level_id) {
        try {
          const response = await axios.get(`${API}/permission-levels/${user.permission_level_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const permLevel = response.data;
          console.log('User permission level:', permLevel);
          
          // Use admin_permissions from the permission level
          const adminPerms = permLevel.admin_permissions || {};
          
          // Check if admin_permissions has any true values
          const hasAnyAdminPerm = Object.values(adminPerms).some(v => v === true);
          
          // If admin_permissions is empty/not set up, and user is admin, grant all
          if (!hasAnyAdminPerm && user.role === 'admin') {
            console.log('Permission level has no admin_permissions set, but user is admin - granting all');
            return {
              dashboard: true, users: true, permissions: true, audit: true,
              'job-duties': true, 'signature-docs': true, 'esignature': true, 'signatures': true, 'declinations-map': true,
              invoices: true, tracking: true, 'report-builder': true, 'pto-analytics': true, 'analytics': true, integrations: true, notifications: true,
              'document-types': true, 'scorecard-criteria': true, 'notification-rules': true
            };
          }
          
          return {
            dashboard: adminPerms.admin_dashboard === true,
            users: adminPerms.admin_users === true,
            permissions: adminPerms.admin_permissions === true,
            audit: adminPerms.admin_audit === true,
            'job-duties': adminPerms.admin_job_duties === true,
            'signature-docs': adminPerms.admin_signatures === true,
            'esignature': adminPerms.admin_signatures === true,
            'signatures': adminPerms.admin_signatures === true,
            'declinations-map': adminPerms.admin_declinations === true,
            invoices: adminPerms.admin_invoices === true,
            tracking: adminPerms.admin_tracking === true || adminPerms.admin_dashboard === true,
            'report-builder': adminPerms.admin_tracking === true || adminPerms.admin_dashboard === true,
            'pto-analytics': adminPerms.admin_dashboard === true,
            'analytics': adminPerms.admin_dashboard === true || adminPerms.admin_tracking === true,
            'applicant-pipeline': adminPerms.admin_dashboard === true,
            integrations: adminPerms.admin_integrations === true,
            'document-types': adminPerms.admin_dashboard === true || adminPerms.admin_tracking === true,
            'scorecard-criteria': adminPerms.admin_dashboard === true || adminPerms.admin_tracking === true,
            'notification-rules': adminPerms.admin_dashboard === true || adminPerms.admin_notifications === true
          };
        } catch (error) {
          console.error('Failed to fetch permission level:', error);
          // If fetch fails but user is admin, grant all permissions as fallback
          if (user.role === 'admin') {
            console.log('Permission fetch failed but user is admin - granting all permissions');
            return {
              dashboard: true, users: true, permissions: true, audit: true,
              'job-duties': true, 'signature-docs': true, 'esignature': true, 'signatures': true, 'declinations-map': true,
              invoices: true, tracking: true, 'report-builder': true, 'pto-analytics': true, 'analytics': true, integrations: true, notifications: true,
              'document-types': true, 'scorecard-criteria': true, 'notification-rules': true
            };
          }
          // Fall through to other checks
        }
      }
      
      // Legacy fallback: If admin role (regardless of permission_level_id), grant all
      if (user.role === 'admin') {
        console.log('User is admin - granting all permissions');
        return {
          dashboard: true, users: true, permissions: true, audit: true,
          'job-duties': true, 'signature-docs': true, 'esignature': true, 'signatures': true, 'declinations-map': true,
          invoices: true, tracking: true, 'report-builder': true, integrations: true, notifications: true,
          'document-types': true
        };
      }
      
      // Check legacy permissions object
      if (user.permissions) {
        const hasAdmin = user.permissions.admin === true;
        console.log('User has legacy permissions, admin:', hasAdmin);
        return {
          dashboard: hasAdmin || user.permissions.dashboard,
          users: hasAdmin,
          permissions: hasAdmin,
          audit: hasAdmin,
          'job-duties': hasAdmin,
          'signature-docs': hasAdmin || user.permissions.view_signature_documents || user.permissions.manage_signature_documents,
          'esignature': hasAdmin || user.permissions.view_signature_documents || user.permissions.manage_signature_documents,
          'signatures': hasAdmin || user.permissions.view_signature_documents || user.permissions.manage_signature_documents,
          'declinations-map': hasAdmin || user.permissions.view_declinations_admin,
          invoices: hasAdmin || user.permissions.view_invoice_verification || user.permissions.verify_invoices,
          integrations: hasAdmin,
          notifications: hasAdmin
        };
      }
      
      console.log('No permissions found for user');
      return {};
    } catch (error) {
      console.error('Failed to get permissions:', error);
      return {};
    }
  }, []);

  // Fetch permission levels for the add user modal
  const fetchPermissionLevels = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/permission-levels`);
      setPermissionLevels(response.data);
    } catch (error) {
      console.error('Failed to fetch permission levels:', error);
    }
  }, []);

  // Load user permissions on mount
  useEffect(() => {
    const loadPermissions = async () => {
      const perms = await getCurrentUserPermissions();
      console.log('Setting user permissions:', perms);
      setUserPermissions(perms);
      
      // Set default tab to first available
      const tabOrder = ['dashboard', 'users', 'permissions', 'audit', 'job-duties', 'signatures', 'signature-docs', 'declinations-map', 'invoices', 'tracking', 'report-builder', 'integrations', 'notifications'];
      const firstAvailable = tabOrder.find(tab => perms[tab]);
      if (firstAvailable && !perms[activeTab]) {
        setActiveTab(firstAvailable);
      }
    };
    
    loadPermissions();
    fetchPermissionLevels();
  }, [getCurrentUserPermissions, fetchPermissionLevels, activeTab]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      }
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, []);

  const fetchAuditLogs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {
        limit: 50,
        offset: auditPage * 50,
        ...(auditFilter.action && { action: auditFilter.action }),
        ...(auditFilter.user_email && { user_email: auditFilter.user_email }),
        ...(auditFilter.resource && { resource: auditFilter.resource })
      };
      const response = await axios.get(`${API}/admin/audit-logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setAuditLogs(response.data.logs);
      setAuditTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    }
  }, [auditPage, auditFilter]);

  const fetchWeeklyMetrics = useCallback(async () => {
    setMetricsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [visitsRes, configRes, projectionsRes, agenciesRes] = await Promise.all([
        axios.get(`${API}/visits`, { headers }),
        axios.get(`${API}/tracking-config`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/projections`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/agency-profiles`, { headers }).catch(() => ({ data: [] })),
      ]);
      const metrics = computeWeeklyMetrics(
        visitsRes.data || [],
        projectionsRes.data || [],
        configRes.data,
        agenciesRes.data || []
      );
      setWeeklyMetrics(metrics);
    } catch (error) {
      console.error('Failed to compute weekly metrics:', error);
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  const fetchOnboardingDigest = useCallback(async () => {
    setDigestLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/onboarding-digest`, { headers: { Authorization: `Bearer ${token}` } });
      setOnboardingDigest(res.data);
    } catch (e) { console.error('Failed to load onboarding digest:', e); }
    finally { setDigestLoading(false); }
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (!isMounted) return;
      setLoading(true);
      await Promise.all([fetchStats(), fetchUsers(), fetchWeeklyMetrics(), fetchOnboardingDigest()]);
      if (isMounted) {
        setLoading(false);
      }
    };
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [fetchStats, fetchUsers, fetchWeeklyMetrics, fetchOnboardingDigest]);

  useEffect(() => {
    let isMounted = true;
    
    if (activeTab === 'audit' && isMounted) {
      fetchAuditLogs();
    }
    
    return () => {
      isMounted = false;
    };
  }, [activeTab, fetchAuditLogs]);

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Access Level is now always required (role auto-derives from it)
    if (!newUser.permission_level_id) {
      toast.error('Please select an access level for the user');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/auth/register`,
        {
          email: newUser.email,
          password: newUser.password,
          first_name: newUser.first_name || null,
          last_name: newUser.last_name || null,
          role: newUser.role,
          permission_level_id: newUser.permission_level_id || null,
          permissions: newUser.permissions
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User created successfully with personnel record');
      setShowAddUser(false);
      setNewUser({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: 'user',
        permission_level_id: '',
        permissions: {
          dashboard: true,
          upload_data: false,
          upload_projections: false,
          upload_credentials: false,
          manage_uploads: false,
          therapists: true,
          agencies: true,
          reports: true,
          map: true,
          admin: false
        }
      });
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create user'));
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/admin/users/${userId}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTempPassword(response.data.temporary_password);
      setShowResetPassword(response.data.user_email);
      toast.success('Password reset successfully');
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reset password'));
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/admin/users/${userId}/toggle-status`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to toggle status'));
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/users/${userId}/role?role=${newRole}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Role changed to ${newRole}`);
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to change role'));
    }
  };

  const handleUnlockUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/users/${userId}/unlock`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User unlocked');
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to unlock user'));
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to delete user ${email}? This cannot be undone.`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted');
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete user'));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  const getActionColor = (action) => {
    if (action.includes('login_success')) return 'text-green-600 bg-green-50';
    if (action.includes('login_failed') || action.includes('failed')) return 'text-red-600 bg-red-50';
    if (action.includes('delete') || action.includes('disabled')) return 'text-red-600 bg-red-50';
    if (action.includes('create') || action.includes('enabled')) return 'text-blue-600 bg-blue-50';
    return 'text-gray-600 bg-gray-50';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div>
        <h1 className="font-heading text-4xl font-semibold text-foreground tracking-tight">
          Admin Console
        </h1>
        <p className="text-muted-foreground mt-2">
          Manage users, view audit logs, and monitor system activity
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 border-b overflow-x-auto pb-1">
        {[
          { id: 'dashboard', label: 'Dashboard', icon: Activity },
          { id: 'users', label: 'Users', icon: Users },
          { id: 'permissions', label: 'Permissions', icon: Shield },
          { id: 'audit', label: 'Audit Logs', icon: FileText },
          { id: 'job-duties', label: 'Job Duties', icon: ClipboardList },
          { id: 'signatures', label: 'Signatures', icon: PenTool },
          { id: 'declinations-map', label: 'Declinations', icon: MapPin },
          { id: 'invoices', label: 'Invoices', icon: Receipt },
          { id: 'tracking', label: 'Tracking', icon: BarChart3 },
          { id: 'report-builder', label: 'Report Builder', icon: FileText },
          { id: 'pto-analytics', label: 'PTO Analytics', icon: Calendar },
          { id: 'analytics', label: 'Analytics', icon: TrendingUp },
          { id: 'applicant-pipeline', label: 'Pipeline', icon: Users },
          { id: 'integrations', label: 'Integrations', icon: Link2 },
          { id: 'notifications', label: 'Notifications', icon: Bell },
          { id: 'document-types', label: 'Document Types', icon: FileText },
          { id: 'scorecard-criteria', label: 'Scorecard Criteria', icon: ClipboardCheck },
          { id: 'notification-rules', label: 'Notification Rules', icon: Bell }
        ].filter(tab => userPermissions[tab.id]).map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              if (tab.id === 'tracking') {
                navigate('/admin/tracking');
                return;
              }
              if (tab.id === 'report-builder') {
                navigate('/admin/report-builder');
                return;
              }
              if (tab.id === 'pto-analytics') {
                navigate('/admin/pto-analytics');
                return;
              }
              if (tab.id === 'analytics') {
                navigate('/admin/analytics');
                return;
              }
              if (tab.id === 'applicant-pipeline') {
                navigate('/admin/applicant-pipeline');
                return;
              }
              if (tab.id === 'document-types') {
                navigate('/admin/document-types');
                return;
              }
              if (tab.id === 'scorecard-criteria') {
                navigate('/admin/scorecard-criteria');
                return;
              }
              if (tab.id === 'notification-rules') {
                navigate('/admin/notification-rules');
                return;
              }
              setActiveTab(tab.id);
            }}
            className={`px-3 py-2 font-medium text-xs border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-sage text-sage bg-sage/5'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
            data-testid={`admin-tab-${tab.id}`}
          >
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && userPermissions.dashboard && stats && (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/site')}
              className="gap-2"
              data-testid="open-site-admin"
            >
              <Globe size={14} /> Edit website pages
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open('/', '_blank')}
              className="gap-2"
              data-testid="view-public-site"
            >
              <ExternalLink size={14} /> View live site
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                  <p className="text-3xl font-bold">{stats.users.total}</p>
                </div>
                <Users className="text-sage" size={32} />
              </div>
            </Card>
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Users</p>
                  <p className="text-3xl font-bold text-green-600">{stats.users.active}</p>
                </div>
                <UserCheck className="text-green-600" size={32} />
              </div>
            </Card>
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Locked Accounts</p>
                  <p className="text-3xl font-bold text-red-600">{stats.users.locked}</p>
                </div>
                <Lock className="text-red-600" size={32} />
              </div>
            </Card>
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Logins (24h)</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.activity.recent_logins_24h}</p>
                </div>
                <Activity className="text-blue-600" size={32} />
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <FileText size={20} />
                Credential Status
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                  <span>Total Therapists</span>
                  <span className="font-bold">{stats.credentials.total_therapists}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                  <span>Total Documents</span>
                  <span className="font-bold">{stats.credentials.total_documents}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                  <span className="flex items-center gap-2">
                    <AlertTriangle className="text-amber-600" size={16} />
                    Expiring Soon (30 days)
                  </span>
                  <span className="font-bold text-amber-600">{stats.credentials.expiring_soon}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                  <span className="flex items-center gap-2">
                    <XCircle className="text-red-600" size={16} />
                    Expired
                  </span>
                  <span className="font-bold text-red-600">{stats.credentials.expired}</span>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Clock size={20} />
                Recent Activity
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {stats.activity.recent_activity.map((log, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 text-sm border-b last:border-0">
                    <div>
                      <span className={`px-2 py-0.5 rounded text-xs ${getActionColor(log.action)}`}>
                        {log.action}
                      </span>
                      <span className="ml-2 text-muted-foreground">{log.user_email}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Quick Links Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/admin/credential-timeline">
              <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer border-2 border-primary/20 hover:border-primary/40" data-testid="quick-link-credential-timeline">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <BarChart3 className="text-primary" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold">Credential Timeline</h4>
                    <p className="text-sm text-muted-foreground">Visual timeline & calendar of credential expirations</p>
                  </div>
                </div>
              </Card>
            </Link>
            <Link to="/admin/onboarding-gallery">
              <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer border-2 border-primary/20 hover:border-primary/40" data-testid="quick-link-onboarding-gallery">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <ClipboardList className="text-primary" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold">Onboarding Gallery</h4>
                    <p className="text-sm text-muted-foreground">Industry template packs for new hire onboarding</p>
                  </div>
                </div>
              </Card>
            </Link>
            <Link to="/admin/visit-classification">
              <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer border-2 border-primary/20 hover:border-primary/40">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <BarChart3 className="text-primary" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold">Visit Classification Debug</h4>
                    <p className="text-sm text-muted-foreground">View all visits with weight (0,1,2) and E/D/MV flags</p>
                  </div>
                </div>
              </Card>
            </Link>
            <Link to="/admin/document-export">
              <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer border-2 border-primary/20 hover:border-primary/40" data-testid="quick-link-document-export">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Download className="text-primary" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold">Document Export</h4>
                    <p className="text-sm text-muted-foreground">Build a download queue across types & people, export as one zip</p>
                  </div>
                </div>
              </Card>
            </Link>
            <Link to="/admin/missing-documents">
              <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer border-2 border-red-200 hover:border-red-400" data-testid="quick-link-missing-documents">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertTriangle className="text-red-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold">Document Recovery Report</h4>
                    <p className="text-sm text-muted-foreground">Files missing from the server — who & what to re-upload, export CSV</p>
                  </div>
                </div>
              </Card>
            </Link>
            <Link to="/admin/careers">
              <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer border-2 border-primary/20 hover:border-primary/40" data-testid="quick-link-careers">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Briefcase className="text-primary" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold">Careers &amp; Site Settings</h4>
                    <p className="text-sm text-muted-foreground">Job postings, application form, company info (phone/email/brand color)</p>
                  </div>
                </div>
              </Card>
            </Link>
            <Link to="/admin/interview-calendar">
              <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer border-2 border-primary/20 hover:border-primary/40" data-testid="quick-link-interview-calendar">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Calendar className="text-primary" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold">Interview Calendar</h4>
                    <p className="text-sm text-muted-foreground">Drag/drop weekly view of all scheduled interviews</p>
                  </div>
                </div>
              </Card>
            </Link>
            <Link to="/admin/schedule-templates">
              <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer border-2 border-primary/20 hover:border-primary/40" data-testid="quick-link-schedule-templates">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Calendar className="text-primary" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold">Schedule Templates</h4>
                    <p className="text-sm text-muted-foreground">Pick which interviewers can be booked for each round (1st, 2nd, Final)</p>
                  </div>
                </div>
              </Card>
            </Link>
            <Link to="/admin/credential-audit">
              <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer border-2 border-primary/20 hover:border-primary/40" data-testid="quick-link-credential-audit">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <ClipboardCheck className="text-primary" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold">Credential Audit</h4>
                    <p className="text-sm text-muted-foreground">Pick any number of therapists, see missing/expired/current at a glance</p>
                  </div>
                </div>
              </Card>
            </Link>
            <Link to="/admin/name-review">
              <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer border-2 border-amber-300/50 hover:border-amber-400" data-testid="quick-link-name-review">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <ClipboardCheck className="text-amber-700" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold">Name review</h4>
                    <p className="text-sm text-muted-foreground">Uploaded clinician names that need a same-person decision</p>
                  </div>
                </div>
              </Card>
            </Link>
          </div>

          {/* Onboarding Digest */}
          <Card className="p-6" data-testid="onboarding-digest">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2 text-lg">
                <ClipboardList size={20} className="text-sage" />
                Onboarding Digest
              </h3>
              <Button variant="outline" size="sm" onClick={fetchOnboardingDigest} disabled={digestLoading} className="gap-1">
                <RefreshCw size={14} className={digestLoading ? 'animate-spin' : ''} /> Refresh
              </Button>
            </div>

            {digestLoading && !onboardingDigest ? (
              <div className="flex items-center justify-center py-8"><RefreshCw className="animate-spin text-sage" size={24} /></div>
            ) : onboardingDigest ? (
              <div className="space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-center">
                    <p className="text-2xl font-bold text-blue-700">{onboardingDigest.summary.total_onboarding}</p>
                    <p className="text-xs text-blue-600">Total</p>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-center">
                    <p className="text-2xl font-bold text-amber-700">{onboardingDigest.summary.in_progress}</p>
                    <p className="text-xs text-amber-600">In Progress</p>
                  </div>
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-center">
                    <p className="text-2xl font-bold text-red-700">{onboardingDigest.summary.stalled}</p>
                    <p className="text-xs text-red-600">Stalled (7+ days)</p>
                  </div>
                  <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-center">
                    <p className="text-2xl font-bold text-green-700">{onboardingDigest.summary.completed}</p>
                    <p className="text-xs text-green-600">Completed</p>
                  </div>
                </div>

                {/* Stalled Alert */}
                {onboardingDigest.stalled.length > 0 && (
                  <div className="border border-red-200 rounded-lg p-3 bg-red-50/50">
                    <h4 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-1"><AlertTriangle size={14} /> Stalled Onboardings</h4>
                    <div className="space-y-1.5">
                      {onboardingDigest.stalled.map((p) => (
                        <div key={p.id} className="flex items-center justify-between text-sm cursor-pointer hover:bg-red-100/50 p-1.5 rounded" onClick={() => navigate(`/personnel/${p.id}`)}>
                          <span className="font-medium">{p.first_name} {p.last_name}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-muted-foreground">{p.completed_items}/{p.total_items} items</span>
                            <div className="w-20 bg-red-200 rounded-full h-1.5"><div className="bg-red-600 h-1.5 rounded-full" style={{ width: `${p.progress_percent}%` }} /></div>
                            <span className="text-xs text-red-700 font-medium">{p.days_since_activity}d inactive</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* In Progress */}
                {onboardingDigest.in_progress.length > 0 && (
                  <div className="border rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-muted-foreground mb-2">In Progress ({onboardingDigest.in_progress.length})</h4>
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                      {onboardingDigest.in_progress.map((p) => (
                        <div key={p.id} className="flex items-center justify-between text-sm cursor-pointer hover:bg-muted/50 p-1.5 rounded" onClick={() => navigate(`/personnel/${p.id}`)}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{p.first_name} {p.last_name}</span>
                            <Badge className="text-[10px] px-1.5 py-0 bg-muted text-muted-foreground">{p.worker_type || '-'}</Badge>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-muted-foreground">{p.completed_items}/{p.total_items}</span>
                            <div className="w-24 bg-muted rounded-full h-1.5"><div className="bg-primary h-1.5 rounded-full transition-all" style={{ width: `${p.progress_percent}%` }} /></div>
                            <span className="text-xs font-medium w-8 text-right">{p.progress_percent}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {onboardingDigest.summary.total_onboarding === 0 && (
                  <div className="text-center py-6 text-muted-foreground">
                    <ClipboardList size={32} className="mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No onboarding activity yet. Start onboarding from any personnel profile.</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">Unable to load onboarding data</p>
            )}
          </Card>

          {/* Weekly Metrics Section - Grouped Tables with Gridlines */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold flex items-center gap-2 text-lg">
                <BarChart3 size={22} className="text-sage" />
                Weekly Metrics
              </h3>
              <div className="flex items-center gap-2">
                {weeklyMetrics && (
                  <span className="text-sm text-muted-foreground">
                    Week Ending: {new Date(weeklyMetrics.week_ending + 'T00:00:00').toLocaleDateString()}
                  </span>
                )}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchWeeklyMetrics}
                  disabled={metricsLoading}
                  className="gap-1"
                >
                  <RefreshCw size={14} className={metricsLoading ? 'animate-spin' : ''} />
                  Refresh
                </Button>
              </div>
            </div>

            {metricsLoading && !weeklyMetrics ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="animate-spin text-sage" size={32} />
              </div>
            ) : weeklyMetrics ? (
              <div className="max-w-md select-all space-y-3" data-testid="weekly-metrics-copyable">
                {/* Table 1: Agency Distribution */}
                <div className="border border-gray-400">
                  <table className="w-full text-xs">
                    <tbody>
                      <tr className="border-b border-gray-400">
                        <td className="border-r border-gray-400 px-2 py-1">Assistcare/Happy at Home:</td>
                        <td className="px-2 py-1 text-right w-16">{weeklyMetrics.assistcare_happy_at_home}</td>
                      </tr>
                      <tr>
                        <td className="border-r border-gray-400 px-2 py-1">All other agencies:</td>
                        <td className="px-2 py-1 text-right w-16">{weeklyMetrics.all_other_agencies}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Table 2: Projections */}
                <div className="border border-gray-400">
                  <table className="w-full text-xs">
                    <tbody>
                      <tr className="border-b border-gray-400">
                        <td className="border-r border-gray-400 px-2 py-1">Final:</td>
                        <td className="px-2 py-1 text-right w-16">{weeklyMetrics.final}</td>
                      </tr>
                      <tr className="border-b border-gray-400">
                        <td className="border-r border-gray-400 px-2 py-1">Proj 1:</td>
                        <td className="px-2 py-1 text-right w-16">{weeklyMetrics.proj_1}</td>
                      </tr>
                      <tr>
                        <td className="border-r border-gray-400 px-2 py-1">Last Week Proj 1:</td>
                        <td className="px-2 py-1 text-right w-16">{weeklyMetrics.last_week_proj_1}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Table 3: Agency Evals and Discharges */}
                <div className="border border-gray-400">
                  <div className="bg-gray-100 border-b border-gray-400 px-2 py-1 text-xs font-medium">
                    Agency Evals and Discharges
                  </div>
                  <table className="w-full text-xs">
                    <tbody>
                      <tr className="border-b border-gray-400">
                        <td className="border-r border-gray-400 px-2 py-1">Total Evals:</td>
                        <td className="px-2 py-1 text-right w-16">{weeklyMetrics.total_evals}</td>
                      </tr>
                      <tr className="border-b border-gray-400">
                        <td className="border-r border-gray-400 px-2 py-1">Total Discharges:</td>
                        <td className="px-2 py-1 text-right w-16">{weeklyMetrics.total_discharges}</td>
                      </tr>
                      <tr>
                        <td className="border-r border-gray-400 px-2 py-1">Difference:</td>
                        <td className="px-2 py-1 text-right w-16">{weeklyMetrics.difference >= 0 ? '+' : ''}{weeklyMetrics.difference}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Table 4: Missed Visits */}
                <div className="border border-gray-400">
                  <div className="bg-gray-100 border-b border-gray-400 px-2 py-1 text-xs font-medium">
                    Missed Visits
                  </div>
                  <table className="w-full text-xs">
                    <tbody>
                      <tr className="border-b border-gray-400">
                        <td className="border-r border-gray-400 px-2 py-1">MV Percentage:</td>
                        <td className="px-2 py-1 text-right w-16">{weeklyMetrics.mv_percentage}%</td>
                      </tr>
                      <tr className="border-b border-gray-400">
                        <td className="border-r border-gray-400 px-2 py-1">Total Notes Completed:</td>
                        <td className="px-2 py-1 text-right w-16">{weeklyMetrics.total_notes}</td>
                      </tr>
                      <tr className="border-b border-gray-400">
                        <td className="border-r border-gray-400 px-2 py-1">Total MV:</td>
                        <td className="px-2 py-1 text-right w-16">{weeklyMetrics.total_mv}</td>
                      </tr>
                      <tr className="border-b border-gray-400">
                        <td className="border-r border-gray-400 px-2 py-1">MVs for Louisiana:</td>
                        <td className="px-2 py-1 text-right w-16">{weeklyMetrics.mv_louisiana}</td>
                      </tr>
                      <tr>
                        <td className="border-r border-gray-400 px-2 py-1">MVs for Texas:</td>
                        <td className="px-2 py-1 text-right w-16">{weeklyMetrics.mv_texas}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <BarChart3 size={48} className="mx-auto mb-4 opacity-50" />
                <p>Unable to load weekly metrics</p>
              </div>
            )}
          </Card>

          {/* Maintenance Section */}
          <Card className="p-6">
            <h3 className="font-semibold flex items-center gap-2 text-lg mb-4">
              <Settings size={20} className="text-sage" />
              Database Maintenance
            </h3>
            <div className="space-y-4">
              {/* Diagnostic Tool */}
              <div className="p-4 border rounded-lg bg-blue-50/50 border-blue-200">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium">Run Diagnostic</h4>
                    <p className="text-sm text-muted-foreground">
                      Check user-personnel linking status and find issues
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const response = await axios.get(
                          `${API}/personnel/diagnostic`,
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        const data = response.data;
                        
                        // Store results in state to display
                        setDiagnosticResults(data);
                        
                        const unlinked = data.users.filter(u => u.issue === 'NO_PERSONNEL_LINKED');
                        const matches = data.potential_matches?.length || 0;
                        
                        if (matches > 0) {
                          toast.info(`${matches} users can be auto-linked!`);
                        } else {
                          toast.success(`Diagnostic complete. ${unlinked.length} users not linked.`);
                        }
                      } catch (error) {
                        toast.error('Failed to run diagnostic');
                        console.error(error);
                      }
                    }}
                    className="gap-2 border-blue-300"
                  >
                    <AlertCircle size={14} />
                    Run Diagnostic
                  </Button>
                </div>
                
                {/* Display diagnostic results */}
                {diagnosticResults && (
                  <div className="mt-4 space-y-4 text-sm">
                    <div className="p-3 bg-white rounded border">
                      <h5 className="font-medium mb-2">Users ({diagnosticResults.users?.length || 0})</h5>
                      <div className="space-y-1 max-h-40 overflow-auto">
                        {diagnosticResults.users?.map((u, i) => (
                          <div key={i} className="flex justify-between items-center py-1 border-b last:border-0">
                            <span>{u.email}</span>
                            <span className={u.personnel_record ? 'text-green-600' : 'text-orange-600'}>
                              {u.personnel_record 
                                ? `→ ${u.personnel_record.first_name} ${u.personnel_record.last_name}`
                                : 'Not linked'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="p-3 bg-white rounded border">
                      <h5 className="font-medium mb-2">
                        Potential Auto-Links ({diagnosticResults.potential_matches?.length || 0})
                      </h5>
                      {diagnosticResults.potential_matches?.length > 0 ? (
                        <div className="space-y-1">
                          {diagnosticResults.potential_matches.map((m, i) => (
                            <div key={i} className="text-green-700">
                              {m.user_email} → {m.personnel.first_name} {m.personnel.last_name}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground">No matching emails found between users and personnel</p>
                      )}
                    </div>
                    
                    <div className="p-3 bg-white rounded border">
                      <h5 className="font-medium mb-2">
                        Sample Personnel Records ({diagnosticResults.total_personnel || 0} total)
                      </h5>
                      <div className="space-y-1 max-h-40 overflow-auto">
                        {diagnosticResults.all_personnel_sample?.map((p, i) => (
                          <div key={i} className="py-1 border-b last:border-0">
                            <span className="font-medium">{p.first_name} {p.last_name}</span>
                            <span className="text-muted-foreground ml-2">({p.email || 'no email'})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Auto-Link Tool */}
              <div className="flex items-center justify-between p-4 border rounded-lg bg-green-50/50 border-green-200">
                <div>
                  <h4 className="font-medium">Auto-Link Users to Personnel</h4>
                  <p className="text-sm text-muted-foreground">
                    Automatically link users to personnel records with matching emails
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const response = await axios.post(
                        `${API}/personnel/auto-link-users`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      const { linked_count, linked_users } = response.data;
                      if (linked_count > 0) {
                        toast.success(`Linked ${linked_count} user(s) to personnel records`);
                        console.log('Linked users:', linked_users);
                      } else {
                        toast.info('No users could be auto-linked (no matching emails found)');
                      }
                    } catch (error) {
                      toast.error('Failed to auto-link users');
                      console.error(error);
                    }
                  }}
                  className="gap-2 border-green-300"
                >
                  <UserCheck size={14} />
                  Auto-Link
                </Button>
              </div>

              {/* Backfill Personnel from Users */}
              <div className="flex items-center justify-between p-4 border rounded-lg bg-blue-50/50 border-blue-200">
                <div>
                  <h4 className="font-medium">Backfill Personnel from Users</h4>
                  <p className="text-sm text-muted-foreground">
                    Create a Personnel record for every legacy admin/user that doesn't have one yet, so they appear in directories, notification pickers, and audit logs.
                  </p>
                </div>
                <Button
                  variant="outline"
                  data-testid="backfill-personnel-btn"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const response = await axios.post(
                        `${API}/personnel/backfill-from-users`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      const { created_count, linked_count, users_evaluated } = response.data;
                      if (created_count > 0 || linked_count > 0) {
                        toast.success(`Created ${created_count} personnel record(s), linked ${linked_count} existing match(es) (out of ${users_evaluated} evaluated)`);
                      } else {
                        toast.info('All users already have personnel records');
                      }
                    } catch (error) {
                      toast.error('Failed to backfill personnel');
                    }
                  }}
                  className="gap-2 border-blue-300"
                >
                  <UserCheck size={14} />
                  Backfill
                </Button>
              </div>

              {/* Verify Durable Storage */}
              <div className="flex items-center justify-between p-4 border rounded-lg bg-emerald-50/50 border-emerald-200">
                <div>
                  <h4 className="font-medium">Verify Durable Storage</h4>
                  <p className="text-sm text-muted-foreground">
                    Confirm every credential file is stored in MongoDB GridFS — the durable, redeploy-proof store. Reports how many are mirrored, how many are still disk-only (vulnerable), and how many are truly lost.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="verify-durable-storage-btn"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const res = await axios.get(
                        `${API}/admin/credentials/durable-storage-status`,
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      const { documents_with_file_path, durably_stored, disk_only, lost, gridfs_files, gridfs_total_bytes, all_durable } = res.data;
                      const mb = (gridfs_total_bytes / (1024 * 1024)).toFixed(1);
                      const msg = `Documents tracked: ${documents_with_file_path}\nDurably stored: ${durably_stored}\nDisk-only (at risk): ${disk_only}\nLost (need re-upload): ${lost}\n\nGridFS: ${gridfs_files} files, ${mb} MB`;
                      if (all_durable) {
                        toast.success(`All ${durably_stored} documents are durably stored. ${mb} MB in GridFS.`, { duration: 8000 });
                      } else if (disk_only > 0) {
                        toast.warning(`${disk_only} document(s) are on disk but NOT in GridFS — run "Backfill Credentials to GridFS" to protect them.\n\n${msg}`, { duration: 12000 });
                      } else {
                        toast.info(msg, { duration: 10000 });
                      }
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to check status');
                    }
                  }}
                  className="border-emerald-300"
                >
                  Verify Now
                </Button>
              </div>

              {/* Cleanup credentials with lost files */}
              <div className="flex items-center justify-between p-4 border rounded-lg bg-amber-50/50 border-amber-200">
                <div>
                  <h4 className="font-medium">Cleanup Credentials With Lost Files</h4>
                  <p className="text-sm text-muted-foreground">
                    Find every credential whose underlying file is missing from both disk and durable storage (lost in a previous redeploy). Archive them (reversible, hidden from audits) OR permanently delete them (the audit will then correctly show those credential types as MISSING). Run dry-run first to see the count.
                  </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="cleanup-lost-files-dryrun-btn"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(
                          `${API}/admin/credentials/cleanup-lost-files`,
                          { mode: 'archive', dry_run: true },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        const { lost_count, scanned } = res.data;
                        toast.info(`Scanned ${scanned} active credential(s) — ${lost_count} have lost files`, { duration: 8000 });
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Dry run failed');
                      }
                    }}
                    className="border-amber-300"
                  >
                    Dry run
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="cleanup-lost-files-btn"
                    onClick={async () => {
                      if (!window.confirm('Archive every credential whose underlying file is missing? Archived records are hidden from audits but reversible from each personnel profile.')) return;
                      try {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(
                          `${API}/admin/credentials/cleanup-lost-files`,
                          { mode: 'archive', dry_run: false },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        const { archived, lost_count, scanned } = res.data;
                        toast.success(`Scanned ${scanned}, archived ${archived} of ${lost_count} lost credential(s)`);
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Cleanup failed');
                      }
                    }}
                    className="border-amber-300"
                  >
                    Archive lost
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="cleanup-lost-files-delete-btn"
                    onClick={async () => {
                      if (!window.confirm('Permanently DELETE every credential record whose underlying file is missing? This cannot be undone — the records are removed from the database entirely, and audits will show those credential types as MISSING for the affected personnel. Use this if you want a clean slate.')) return;
                      try {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(
                          `${API}/admin/credentials/cleanup-lost-files`,
                          { mode: 'delete', dry_run: false },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        const { deleted, lost_count, scanned } = res.data;
                        toast.success(`Scanned ${scanned}, deleted ${deleted} of ${lost_count} lost credential(s)`);
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Cleanup failed');
                      }
                    }}
                    className="text-destructive border-destructive/40 hover:bg-destructive/10"
                  >
                    Delete lost
                  </Button>
                </div>
              </div>
              
              {/* Cleanup Tool */}
              <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                <div>
                  <h4 className="font-medium">Cleanup Orphan Personnel Records</h4>
                  <p className="text-sm text-muted-foreground">
                    Remove personnel records with empty names and re-link users to their correct profiles
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  data-testid="cleanup-orphans-btn"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const response = await axios.post(
                        `${API}/personnel/cleanup-orphans`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      const { deleted_count, relinked_count } = response.data;
                      if (deleted_count > 0 || relinked_count > 0) {
                        toast.success(`Cleaned up ${deleted_count} orphan record(s), re-linked ${relinked_count} user(s)`);
                      } else {
                        toast.info('No orphan records found');
                      }
                    } catch (error) {
                      toast.error('Failed to cleanup orphan records');
                    }
                  }}
                  className="gap-2"
                >
                  <Trash2 size={14} />
                  Run Cleanup
                </Button>
              </div>

              {/* Duplicate Visits Cleanup */}
              <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                <div>
                  <h4 className="font-medium">Cleanup Duplicate Visits</h4>
                  <p className="text-sm text-muted-foreground">
                    Remove visit records that were duplicated by re-uploading the same report file. Keeps the earliest batch and removes later duplicates.
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  data-testid="cleanup-duplicates-btn"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const response = await axios.post(
                        `${API}/visits/cleanup-duplicates`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      const { total_removed, details } = response.data;
                      if (total_removed > 0) {
                        toast.success(`Removed ${total_removed} duplicate visit(s)`);
                      } else {
                        toast.info('No duplicate visits found');
                      }
                    } catch (error) {
                      toast.error('Failed to cleanup duplicate visits');
                    }
                  }}
                  className="gap-2"
                >
                  <Trash2 size={14} />
                  Cleanup Duplicates
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Users size={20} />
              User Management
            </h3>
            <div className="flex gap-2">
              <Button onClick={() => navigate('/personnel')} className="gap-2" data-testid="add-user-via-personnel-btn">
                <Plus size={16} />
                Add Person + Login
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowAddUser(true)} title="Legacy email-only user (no personnel record)" data-testid="add-user-legacy-btn">
                Email-only User
              </Button>
              <Button variant="outline" size="sm" onClick={fetchUsers}>
                <RefreshCw size={14} />
              </Button>
            </div>
          </div>

          <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-900 flex items-start gap-2">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">Unified user creation</p>
              <p className="text-xs text-blue-800 mt-0.5">
                For new admins or staff, use <b>Add Person + Login</b> on the Personnel page — it creates the personnel record and login account in one step, with an Admin / User toggle.
                The "Email-only User" option remains for cases where you don't need a personnel record (e.g., external auditor).
              </p>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-medium">Email</th>
                  <th className="text-left p-3 font-medium">Role</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-left p-3 font-medium">Last Login</th>
                  <th className="text-left p-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} className="border-b hover:bg-muted/30">
                    <td className="p-3">{user.email}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5" title="Role is auto-derived from the user's Access Level. Edit Access Level on the Permission Management page to change role.">
                        {user.role === 'admin' ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-amber-100 text-amber-800 border border-amber-200">
                            <Shield size={11} /> Admin
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 border border-slate-200">
                            <Users size={11} /> User
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      {user.locked_until ? (
                        <span className="flex items-center gap-1 text-red-600">
                          <Lock size={14} /> Locked
                        </span>
                      ) : user.is_active === false ? (
                        <span className="flex items-center gap-1 text-gray-500">
                          <UserX size={14} /> Disabled
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle size={14} /> Active
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-sm text-muted-foreground">
                      {formatDate(user.last_login)}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        <Link to="/admin/permissions">
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Manage Permissions"
                          >
                            <Settings size={14} />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResetPassword(user.id)}
                          title="Reset Password"
                        >
                          <Key size={14} />
                        </Button>
                        {user.locked_until && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnlockUser(user.id)}
                            title="Unlock"
                          >
                            <Unlock size={14} />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleStatus(user.id)}
                          title={user.is_active === false ? "Enable" : "Disable"}
                        >
                          {user.is_active === false ? <UserCheck size={14} /> : <UserX size={14} />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteUser(user.id, user.email)}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Audit Logs Tab */}
      {activeTab === 'audit' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <FileText size={20} />
              Audit Logs
            </h3>
            <div className="flex gap-2 flex-wrap">
              <Select
                value={auditFilter.resource || 'all'}
                onValueChange={(v) => setAuditFilter(f => ({ ...f, resource: v === 'all' ? '' : v }))}
              >
                <SelectTrigger className="w-[140px] h-9">
                  <SelectValue placeholder="All resources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All resources</SelectItem>
                  <SelectItem value="personnel">Personnel</SelectItem>
                  <SelectItem value="projections">Projections</SelectItem>
                  <SelectItem value="visits">Visits</SelectItem>
                  <SelectItem value="credentials">Credentials</SelectItem>
                  <SelectItem value="auth">Authentication</SelectItem>
                  <SelectItem value="users">Users</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 text-muted-foreground" size={14} />
                <Input
                  placeholder="Filter by action..."
                  className="pl-8 w-40 h-9"
                  value={auditFilter.action}
                  onChange={(e) => setAuditFilter(f => ({ ...f, action: e.target.value }))}
                />
              </div>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 text-muted-foreground" size={14} />
                <Input
                  placeholder="Filter by email..."
                  className="pl-8 w-48 h-9"
                  value={auditFilter.user_email}
                  onChange={(e) => setAuditFilter(f => ({ ...f, user_email: e.target.value }))}
                />
              </div>
              <Button variant="outline" size="sm" onClick={fetchAuditLogs}>
                <RefreshCw size={14} />
              </Button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-medium">Timestamp</th>
                  <th className="text-left p-3 font-medium">User</th>
                  <th className="text-left p-3 font-medium">Action</th>
                  <th className="text-left p-3 font-medium">Resource</th>
                  <th className="text-left p-3 font-medium">Details</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((log, idx) => (
                  <tr key={idx} className="border-b hover:bg-muted/30">
                    <td className="p-3 text-sm whitespace-nowrap">{formatDate(log.timestamp)}</td>
                    <td className="p-3 text-sm">{log.user_email}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs ${getActionColor(log.action)}`}>
                        {log.action.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="p-3 text-sm">{log.resource}</td>
                    <td className="p-3 text-sm text-muted-foreground" title={log.details}>
                      {log.details || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-muted-foreground">
              Showing {auditPage * 50 + 1} - {Math.min((auditPage + 1) * 50, auditTotal)} of {auditTotal}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={auditPage === 0}
                onClick={() => setAuditPage(p => p - 1)}
              >
                <ChevronLeft size={14} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={(auditPage + 1) * 50 >= auditTotal}
                onClick={() => setAuditPage(p => p + 1)}
              >
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Job Duties Tab */}
      {activeTab === 'job-duties' && (
        <Suspense fallback={<div className="p-8 text-center text-muted-foreground">Loading...</div>}>
          <JobDuties embedded={true} />
        </Suspense>
      )}

      {/* Signatures (unified) Tab */}
      {activeTab === 'signatures' && (
        <Suspense fallback={<div className="p-8 text-center text-muted-foreground">Loading...</div>}>
          <ESignatureManagement />
        </Suspense>
      )}

      {/* Declinations Map Tab */}
      {activeTab === 'declinations-map' && (
        <Suspense fallback={<div className="p-8 text-center text-muted-foreground">Loading...</div>}>
          <DeclinationsMap embedded={true} />
        </Suspense>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <Suspense fallback={<div className="p-8 text-center text-muted-foreground">Loading...</div>}>
          <InvoiceVerification embedded={true} />
        </Suspense>
      )}

      {/* Permissions Tab */}
      {activeTab === 'permissions' && (
        <Suspense fallback={<div className="p-8 text-center text-muted-foreground">Loading...</div>}>
          <PermissionManagement embedded={true} />
        </Suspense>
      )}

      {/* Integrations Tab */}
      {activeTab === 'integrations' && (
        <Suspense fallback={<div className="p-8 text-center text-muted-foreground">Loading...</div>}>
          <ExternalIntegrations embedded={true} />
        </Suspense>
      )}

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Add New User</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowAddUser(false)}>
                <X size={16} />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name</Label>
                  <Input
                    value={newUser.first_name}
                    onChange={(e) => setNewUser(u => ({ ...u, first_name: e.target.value }))}
                    placeholder="John"
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={newUser.last_name}
                    onChange={(e) => setNewUser(u => ({ ...u, last_name: e.target.value }))}
                    placeholder="Doe"
                  />
                </div>
              </div>
              
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser(u => ({ ...u, email: e.target.value }))}
                  placeholder="user@example.com"
                />
              </div>
              
              <div>
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser(u => ({ ...u, password: e.target.value }))}
                  placeholder="Min 8 chars, uppercase, lowercase, number"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Must be 8+ characters with uppercase, lowercase, and number
                </p>
              </div>
              
              <div>
                <Label>Access Level *</Label>
                <Select
                  value={newUser.permission_level_id}
                  onValueChange={(val) => {
                    const lvl = permissionLevels.find(l => l.id === val);
                    const grantsAdmin = !!(lvl?.is_admin || (lvl?.admin_permissions || {}).admin_users);
                    setNewUser(u => ({
                      ...u,
                      permission_level_id: val,
                      role: grantsAdmin ? 'admin' : 'user',
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select access level..." />
                  </SelectTrigger>
                  <SelectContent>
                    {permissionLevels.map(level => (
                      <SelectItem key={level.id} value={level.id}>
                        {level.name}
                        {level.description && (
                          <span className="text-muted-foreground ml-2">- {level.description}</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Role (Admin / User) is set automatically based on this access level.
                </p>
              </div>
              
              <div className="flex gap-2 justify-end pt-4">
                <Button variant="outline" onClick={() => setShowAddUser(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddUser}>
                  Create User
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="space-y-6">
          <NotificationSettings
            settings={notifSettings}
            setSettings={setNotifSettings}
            personnel={notifPersonnel}
            saving={savingNotif}
            setSaving={setSavingNotif}
          />
          <CredentialReminders />
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="p-6 max-w-md w-full mx-4">
            <h3 className="font-semibold text-lg mb-4">Password Reset</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Temporary password for <strong>{showResetPassword}</strong>:
            </p>
            <div className="bg-muted p-3 rounded-lg font-mono text-center text-lg mb-4">
              {tempPassword}
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Please share this password securely with the user. They will be prompted to change it on next login.
            </p>
            <Button 
              className="w-full" 
              onClick={() => {
                navigator.clipboard.writeText(tempPassword);
                toast.success('Password copied to clipboard');
                setShowResetPassword(null);
                setTempPassword('');
              }}
            >
              Copy & Close
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
}
